import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';
import useTenantState from './tenant.state';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { normalizeDivisionKey, normalizeLevelKey } from '@/utils/functions/organization.utils';

export interface BulkOrgChartNode {
  tempKey: string;
  id?: number;
  name: string;
  division_key: string;
  level_key: string;
  description?: string | null;
  parentTempKey?: string | null;
}

export interface IOrgChart {
  id: number;
  name: string;
  description?: string;
  is_published: boolean;
  tenant_id: number;
  division_key?: string;
  level_key?: string;
  parent_id?: number | null;
  created_at?: string;
  updated_at?: string;
  children?: IOrgChart[];
}

interface OrgChartState {
  orgCharts: IOrgChart[];
  isLoading: boolean;
  error: unknown;
  hasOrgChart: boolean;

  fetchOrgCharts: (tenantId: string | number) => Promise<IOrgChart[]>;
  fetchOrgChartTree: (tenantId: string | number) => Promise<IOrgChart | null>;
  fetchOrgChartSubtree: (parentId: number) => Promise<IOrgChart | null>;
  createOrgChart: (formData: Partial<IOrgChart>) => Promise<void>;
  createChildOrgChart: (parentId: number, formData: Partial<IOrgChart>) => Promise<void>;
  updateOrgChart: (id: number, formData: Partial<IOrgChart>) => Promise<void>;
  deleteOrgChart: (id: number) => Promise<void>;
  resetOrgChart: (tenantId: string | number) => Promise<void>;
  clearOrgCharts: () => void;
  bulkUpsertOrgChart: (tenantId: string | number, nodes: BulkOrgChartNode[]) => Promise<void>;
}

const useOrgChartState = create<OrgChartState>(set => ({
  orgCharts: [],
  isLoading: false,
  error: null,
  hasOrgChart: false,

  clearOrgCharts: () => set({ orgCharts: [] }),

  fetchOrgCharts: async tenantId => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${tenantId}`;
      const { data } = await axiosInstance.get(url);

      const list = Array.isArray(data) ? data : [data];
      set({
        orgCharts: list,
        isLoading: false,
        hasOrgChart: list.length > 0,
      });

      return list;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;

      if (status === 404) {
        set({
          orgCharts: [],
          isLoading: false,
          hasOrgChart: false,
          error: null,
        });
        return [];
      }

      errorHandler(error);
      set({
        orgCharts: [],
        isLoading: false,
        hasOrgChart: false,
        error,
      });
      return [];
    }
  },

  fetchOrgChartTree: async tenantId => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${tenantId}/tree`;
      const { data } = await axiosInstance.get(url);

      set({
        orgCharts: Array.isArray(data) ? data : [data],
        isLoading: false,
        hasOrgChart: Array.isArray(data) ? data.length > 0 : !!data,
      });

      return data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;

      if (status === 404) {
        set({
          orgCharts: [],
          isLoading: false,
          hasOrgChart: false,
          error: null,
        });
        return null;
      }

      errorHandler(error);
      set({
        orgCharts: [],
        isLoading: false,
        hasOrgChart: false,
        error,
      });
      return null;
    }
  },

  fetchOrgChartSubtree: async parentId => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${parentId}/subtree`;
      const { data } = await axiosInstance.get(url);

      set({ isLoading: false });
      return data;
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
      return null;
    }
  },

  createOrgChart: async formData => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    if (!tenantId) return;

    set({ isLoading: true });
    try {
      const divisionKey = normalizeDivisionKey(formData.division_key);
      const levelKey = normalizeLevelKey(formData.level_key);

      if (!divisionKey || !levelKey) {
        toast.error('Divisi dan level jabatan wajib dipilih');
        set({ isLoading: false });
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        is_published: formData.is_published ?? true,
        tenant_id: tenantId,
        parent_id: formData.parent_id ?? null,
        division_key: divisionKey,
        level_key: levelKey,
      };

      await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart`, payload);
      toast.success('Struktur organisasi induk berhasil dibuat');

      const updated = await useOrgChartState.getState().fetchOrgCharts(tenantId);
      set({
        isLoading: false,
        hasOrgChart: updated.length > 0,
      });
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
    }
  },

  createChildOrgChart: async (parentId, formData) => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    if (!tenantId) return;

    set({ isLoading: true });
    try {
      const divisionKey = normalizeDivisionKey(formData.division_key);
      const levelKey = normalizeLevelKey(formData.level_key);

      if (!divisionKey || !levelKey) {
        toast.error('Divisi dan level jabatan wajib dipilih');
        set({ isLoading: false });
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        is_published: formData.is_published ?? true,
        tenant_id: tenantId,
        parent_id: parentId,
        division_key: divisionKey,
        level_key: levelKey,
      };

      await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart`, payload);
      toast.success('Struktur organisasi turunan berhasil dibuat');

      const updated = await useOrgChartState.getState().fetchOrgCharts(tenantId);
      set({
        isLoading: false,
        hasOrgChart: updated.length > 0,
      });
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
    }
  },

  updateOrgChart: async (id, formData) => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    if (!tenantId) return;

    set({ isLoading: true });
    try {
      const divisionKey = normalizeDivisionKey(formData.division_key);
      const levelKey = normalizeLevelKey(formData.level_key);

      if (!divisionKey || !levelKey) {
        toast.error('Divisi dan level jabatan wajib dipilih');
        set({ isLoading: false });
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        is_published: formData.is_published ?? true,
        tenant_id: tenantId,
        parent_id: formData.parent_id ?? null,
        division_key: divisionKey,
        level_key: levelKey,
      };

      await axiosInstance.put(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${id}`, payload);
      toast.success('Struktur organisasi berhasil diperbarui');

      const updated = await useOrgChartState.getState().fetchOrgCharts(tenantId);
      set({
        isLoading: false,
        hasOrgChart: updated.length > 0,
      });
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
    }
  },

  deleteOrgChart: async id => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    if (!tenantId) return;

    set({ isLoading: true });
    try {
      await axiosInstance.delete(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/${id}`);
      toast.success('Struktur organisasi berhasil dihapus');

      const updated = await useOrgChartState.getState().fetchOrgCharts(tenantId);
      set({
        isLoading: false,
        hasOrgChart: updated.length > 0,
      });
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
    }
  },

  resetOrgChart: async tenantId => {
    set({ isLoading: true });
    try {
      await axiosInstance.delete(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${tenantId}`);
      set({
        orgCharts: [],
        hasOrgChart: false,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
    }
  },

  bulkUpsertOrgChart: async (tenantId, nodes) => {
    set({ isLoading: true });
    try {
      await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/compro/org-chart/tenant/${tenantId}/bulk`, { nodes });
      toast.success('Struktur organisasi berhasil disimpan');
      const updated = await useOrgChartState.getState().fetchOrgCharts(tenantId);
      set({ isLoading: false, hasOrgChart: updated.length > 0 });
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
      throw err;
    }
  },
}));

export default useOrgChartState;
